import { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import tw from 'tailwind.macro';
import {
  disableBodyScroll,
  enableBodyScroll,
} from '../../helpers/windowScroll';
import loadingBag from '../../images/illustrations/rounded-bag.webp';
import loadingBag2x from '../../images/illustrations/rounded-bag-2x.webp';
import loadingBag3x from '../../images/illustrations/rounded-bag-3x.webp';
import loadingBus from '../../images/illustrations/rounded-bus.webp';
import loadingBus2x from '../../images/illustrations/rounded-bus-2x.webp';
import loadingBus3x from '../../images/illustrations/rounded-bus-3x.webp';
import loadingCar from '../../images/illustrations/rounded-car.webp';
import loadingCar2x from '../../images/illustrations/rounded-car-2x.webp';
import loadingCar3x from '../../images/illustrations/rounded-car-3x.webp';
import loadingFlag from '../../images/illustrations/rounded-flag.webp';
import loadingFlag2x from '../../images/illustrations/rounded-flag-2x.webp';
import loadingFlag3x from '../../images/illustrations/rounded-flag-3x.webp';
import loadingTicket from '../../images/illustrations/rounded-ticket.webp';
import loadingTicket2x from '../../images/illustrations/rounded-ticket-2x.webp';
import loadingTicket3x from '../../images/illustrations/rounded-ticket-3x.webp';
import loadingTrain from '../../images/illustrations/rounded-train.webp';
import loadingTrain2x from '../../images/illustrations/rounded-train-2x.webp';
import loadingTrain3x from '../../images/illustrations/rounded-train-3x.webp';
import { loadingModalTestid } from './constants';

const Container = styled.div<{ option: LoadingModalOption }>`
  ${tw`w-full flex px-medium2`}

  ${({ option }) =>
    option.withBackground &&
    css`
      ${tw`bg-overlay`}
    `}

    ${({ option }) =>
    option.isFixed &&
    css`
      ${tw`z-5 fixed md:pt-0`}

      inset: 0;
    `}
`;

const Wrapper = styled.div<{ option: LoadingModalOption }>`
  ${tw`relative w-full shadow-medium bg-white py-big px-medium rounded-30 flex flex-col items-center
 md:shadow-light`}
  height:max-content;
  max-width: 474px;
  margin: auto;

  gap: var(--size-big);
`;

const Image = styled.img.attrs((props) => ({
  srcSet: props.srcSet,
}))`
  ${tw`max-w-full`}
`;

const ImagesWrapper = styled.div`
  ${tw`relative`}
`;

const TitleContainer = styled.div`
  ${tw`absolute w-full bg-white shadow-strong px-medium py-tiny rounded-30`}
  bottom:0;
`;

const Text = styled.div`
  ${tw`
        text-p-medium leading-p-medium text-center
        lg:text-p-medium lg:leading-p-medium
    `}
`;

const IMAGES = [
  {
    src: loadingTicket,
    srcSet: `${loadingTicket2x} 2x, ${loadingTicket3x} 3x`,
  },
  { src: loadingBag, srcSet: `${loadingBag2x} 2x, ${loadingBag3x} 3x` },
  { src: loadingBus, srcSet: `${loadingBus2x} 2x, ${loadingBus3x} 3x` },
  { src: loadingCar, srcSet: `${loadingCar2x} 2x, ${loadingCar3x} 3x` },
  { src: loadingTrain, srcSet: `${loadingTrain2x} 2x, ${loadingTrain3x} 3x` },
  { src: loadingFlag, srcSet: `${loadingFlag2x} 2x, ${loadingFlag3x} 3x` },
];

type LoadingModalOption = {
  isFixed: boolean;
  withBackground: boolean;
  canScroll: boolean;
};

type LoadingModalProps = {
  messages: React.ReactNode;
  title?: React.ReactNode;
  option?: LoadingModalOption;
};

const LoadingModal = ({
  messages,
  option = { isFixed: true, withBackground: true, canScroll: false },
  title,
}: LoadingModalProps) => {
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);

  useEffect(() => {
    if (!option.canScroll) {
      disableBodyScroll();
    }

    return () => {
      if (!option.canScroll) {
        enableBodyScroll();
      }
    };
  }, [option.canScroll]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % IMAGES.length);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  return (
    <Container data-testid={loadingModalTestid} option={option}>
      <Wrapper option={option}>
        <ImagesWrapper>
          {IMAGES.map((image, index) => (
            <Image
              key={index}
              alt="loading"
              src={image.src}
              srcSet={image.srcSet}
              style={(index !== currentImageIndex && tw`hidden`) || null}
            />
          ))}
          {title && <TitleContainer>{title}</TitleContainer>}
        </ImagesWrapper>

        <Text>{messages}</Text>
      </Wrapper>
    </Container>
  );
};

export default LoadingModal;
